import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LoadingComponent } from '@scandium-oy/ngx-scandium';
import { addDays, addWeeks, endOfWeek, format, startOfToday, startOfWeek } from 'date-fns';
import { fi } from 'date-fns/locale';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { CalendarComponent } from '../calendar/component/calendar.component';
import { AppCommonModule } from '../common.module';
import { MobileFilterCollapseComponent } from '../components/mobile-filter-collapse/mobile-filter-collapse.component';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { DashboardViewComponent } from '../components/views/dashboard/view-dashboard.component';
import { Client } from '../models/client.model';
import { SiteProjectPeriod } from '../models/site-project-period.model';
import { SiteProject } from '../models/site-project.model';
import { Site } from '../models/site.model';
import { TicketStatus } from '../models/ticket.model';
import { ClientsService } from '../services/clients.service';
import { ContractorsService } from '../services/contractors.service';
import { NavigationService } from '../services/navigation.service';
import { SiteProjectPeriodsService } from '../services/site-project-period.service';
import { SiteProjectsService } from '../services/site-projects.service';
import { SitesService } from '../services/sites.service';
import { UsersService } from '../services/users.service';
import { WorkerTicketsService } from '../services/worker-tickets.service';
import { InsightCardItem, InsightCardsComponent } from '../ui/insight-cards/insight-cards.component';
import { PageHeaderActionsComponent } from '../ui/page-header-actions/page-header-actions.component';
import { SectionTabItem, SectionTabsComponent } from '../ui/section-tabs/section-tabs.component';
import { defaultProjectName } from '../utility/kanban';
import { Roles } from '../utility/role';
import { sortIgnoreCase } from '../utility/sort';
import { convertTimestamp } from '../utility/time';

type ProjectStatus = 'active' | 'upcoming' | 'late' | 'completed';
type OverviewTab = 'overview' | 'calendar' | 'tracking' | 'finance';
type PhaseStatus = 'active' | 'upcoming' | 'late' | 'completed';

interface ProjectRowVm {
  project: SiteProject;
  site: Site | null;
  client: Client | null;
  clientGuid: string | null;
  siteGuid: string | null;
  clientName: string;
  siteName: string;
  responsible: string;
  startDate: Date | null;
  endDate: Date | null;
  status: ProjectStatus;
  financialState: 'billable' | 'notBillable';
  progressPercent: number;
  completedTasks: number;
  totalTasks: number;
  phases: ProjectPhaseVm[];
  isDemo?: boolean;
}

interface ProjectPhaseVm {
  guid: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  status: PhaseStatus;
  totalTasks: number;
  completedTasks: number;
}

interface TrackingCardVm {
  titleKey: string;
  items: ProjectRowVm[];
  emptyMessageKey: string;
}

interface FinanceCardVm {
  titleKey: string;
  value: number;
  items: ProjectRowVm[];
  emptyMessageKey: string;
}

interface GuidOption {
  guid: string;
  name: string;
}

@Component({
  selector: 'app-projects-page',
  templateUrl: './projects.page.html',
  styleUrls: ['./projects.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    DashboardViewComponent,
    CalendarComponent,
    LoadingComponent,
    NoResultsComponent,
    MobileFilterCollapseComponent,
    InsightCardsComponent,
    PageHeaderActionsComponent,
    SectionTabsComponent,
  ],
})
export class ProjectsPage {
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private navigationService = inject(NavigationService);
  private siteProjectPeriodsService = inject(SiteProjectPeriodsService);
  private siteProjectsService = inject(SiteProjectsService);
  private sitesService = inject(SitesService);
  private translateService = inject(TranslateService);
  private workerTicketsService = inject(WorkerTicketsService);
  private usersService = inject(UsersService);

  searchQuery = signal('');
  statusFilter = signal<'all' | ProjectStatus>('all');
  clientFilter = signal('all');
  siteFilter = signal('all');
  ownerFilter = signal('all');
  activeTab = signal<OverviewTab>('overview');
  selectedDay = signal<Date>(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  calendarView = signal<'day' | 'week'>('week');
  loading = signal(true);
  isManager = computed(() => [Roles.manager, Roles.managerWorker].includes(this.usersService.role()));

  readonly sectionTabs: SectionTabItem[] = [
    { value: 'overview', labelKey: 'projectsPage.tabs.overview' },
    { value: 'calendar', labelKey: 'projectsPage.tabs.calendar' },
    { value: 'tracking', labelKey: 'projectsPage.tabs.tracking' },
    { value: 'finance', labelKey: 'projectsPage.tabs.finance', show: !this.isManager() },
  ];

  calendarPeriodLabel = computed(() => {
    const day = this.selectedDay();
    if (this.calendarView() === 'day') {
      return format(day, 'd.M.yyyy', { locale: fi });
    }
    const start = startOfWeek(day, { weekStartsOn: 1 });
    const end = endOfWeek(day, { weekStartsOn: 1 });
    return this.translateService.instant('projectsPage.calendar.weekLabel', {
      week: format(start, 'w', { locale: fi }),
      start: format(start, 'd.M.', { locale: fi }),
      end: format(end, 'd.M.yyyy', { locale: fi }),
    });
  });

  private sites$ = this.sitesService.getActiveList().pipe(
    filter((sites) => sites?.length > 0),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private projects$ = combineLatest([this.contractorsService.getCurrentContractor(), this.sitesService.getCurrentSite$()]).pipe(
    filter(([contractor, currentSite]) => contractor != null || currentSite != null),
    switchMap(([contractor, currentSite]) => this.siteProjectsService.getList({ contractor: contractor?.guid, site: currentSite?.guid })),
    map((projects) => projects.filter((project) => project.name !== defaultProjectName)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private clients$ = this.clientsService.getList().pipe(shareReplay(1));

  projectRows$ = combineLatest([this.projects$, this.sites$, this.clients$]).pipe(
    switchMap(([projects, sites, clients]) => {
      return combineLatest(projects.map((project) => this.buildProjectRow(project, sites, clients)));
    }),
    map((rows) => rows.sort((a, b) => sortIgnoreCase(a.project, b.project, 'name'))),
    tap(() => this.loading.set(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  filteredRows$ = combineLatest([
    this.projectRows$,
    toObservable(this.searchQuery),
    toObservable(this.statusFilter),
    toObservable(this.clientFilter),
    toObservable(this.siteFilter),
    toObservable(this.ownerFilter),
  ]).pipe(
    map(([rows, searchQuery, statusFilter, clientFilter, siteFilter, ownerFilter]) => {
      const query = searchQuery.trim().toLowerCase();
      return rows.filter((row) => {
        const matchesQuery = !query
          || row.project.name.toLowerCase().includes(query)
          || row.siteName.toLowerCase().includes(query)
          || row.clientName.toLowerCase().includes(query);
        const matchesStatus = statusFilter === 'all' || row.status === statusFilter;
        const matchesClient = clientFilter === 'all' || row.clientGuid === clientFilter;
        const matchesSite = siteFilter === 'all' || row.siteGuid === siteFilter;
        const matchesOwner = ownerFilter === 'all' || row.responsible === ownerFilter;
        return matchesQuery && matchesStatus && matchesClient && matchesSite && matchesOwner;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  overviewKpis$ = this.projectRows$.pipe(
    map((rows) => [
      {
        key: 'active',
        titleKey: 'projectsPage.kpi.active',
        value: rows.filter((row) => row.status === 'active').length,
        icon: 'play-circle-outline',
        tone: 'blue',
        descriptionKey: 'projectsPage.kpiDescriptions.active',
        helperKey: 'projectsPage.kpiDataScope.projectStatus',
        clickable: true,
      },
      {
        key: 'upcoming',
        titleKey: 'projectsPage.kpi.upcoming',
        value: rows.filter((row) => row.status === 'upcoming').length,
        icon: 'time-outline',
        tone: 'purple',
        descriptionKey: 'projectsPage.kpiDescriptions.upcoming',
        helperKey: 'projectsPage.kpiDataScope.projectStatus',
        clickable: true,
      },
      {
        key: 'late',
        titleKey: 'projectsPage.kpi.late',
        value: rows.filter((row) => row.status === 'late').length,
        icon: 'warning-outline',
        tone: 'red',
        descriptionKey: 'projectsPage.kpiDescriptions.late',
        helperKey: 'projectsPage.kpiDataScope.projectStatus',
        clickable: true,
      },
      {
        key: 'billable',
        titleKey: 'projectsPage.kpi.billable',
        value: rows.filter((row) => row.financialState === 'billable').length,
        icon: 'receipt-outline',
        tone: 'orange',
        descriptionKey: 'projectsPage.kpiDescriptions.billable',
        helperKey: 'projectsPage.kpiDataScope.contractValueBased',
        clickable: true,
      },
      {
        key: 'completed',
        titleKey: 'projectsPage.kpi.completed',
        value: rows.filter((row) => row.status === 'completed').length,
        icon: 'checkmark-circle-outline',
        tone: 'grey',
        descriptionKey: 'projectsPage.kpiDescriptions.completed',
        helperKey: 'projectsPage.kpiDataScope.projectStatus',
        clickable: true,
      },
    ] satisfies InsightCardItem[]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  clientOptions$ = this.projectRows$.pipe(
    map((rows) => {
      const options = new Map<string, GuidOption>();
      rows.forEach((row) => {
        if (row.clientGuid && !options.has(row.clientGuid)) {
          options.set(row.clientGuid, { guid: row.clientGuid, name: row.clientName });
        }
      });
      return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  siteOptions$ = this.projectRows$.pipe(
    map((rows) => {
      const options = new Map<string, GuidOption>();
      rows.forEach((row) => {
        if (row.siteGuid && !options.has(row.siteGuid)) {
          options.set(row.siteGuid, { guid: row.siteGuid, name: row.siteName });
        }
      });
      return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ownerOptions$ = this.projectRows$.pipe(
    map((rows) => Array.from(new Set(rows.map((row) => row.responsible).filter((name) => name !== '-'))).sort((a, b) => a.localeCompare(b))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  calendarSites$ = combineLatest([this.filteredRows$, this.sites$]).pipe(
    map(([rows, sites]) => {
      const siteIds = new Set(rows.map((row) => row.site?.guid).filter((guid): guid is string => !!guid));
      return sites
        .filter((site) => siteIds.has(site.guid))
        .sort((a, b) => sortIgnoreCase(a, b, 'project'));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  trackingCards$ = this.filteredRows$.pipe(
    map((rows) => [
      {
        titleKey: 'projectsPage.tracking.late.title',
        items: rows.filter((row) => row.status === 'late').slice(0, 4),
        emptyMessageKey: 'projectsPage.tracking.late.empty',
      },
      {
        titleKey: 'projectsPage.tracking.upcoming.title',
        items: rows.filter((row) => row.status === 'upcoming').slice(0, 4),
        emptyMessageKey: 'projectsPage.tracking.upcoming.empty',
      },
      {
        titleKey: 'projectsPage.tracking.active.title',
        items: rows.filter((row) => row.status === 'active').slice(0, 4),
        emptyMessageKey: 'projectsPage.tracking.active.empty',
      },
    ] satisfies TrackingCardVm[]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  financeCards$ = this.filteredRows$.pipe(
    map((rows) => [
      {
        titleKey: 'projectsPage.finance.billable.title',
        value: rows.filter((row) => row.financialState === 'billable').length,
        items: rows.filter((row) => row.financialState === 'billable').slice(0, 4),
        emptyMessageKey: 'projectsPage.finance.billable.empty',
      },
      {
        titleKey: 'projectsPage.finance.lateEnding.title',
        value: rows.filter((row) => row.status === 'late').length,
        items: rows.filter((row) => row.status === 'late').slice(0, 4),
        emptyMessageKey: 'projectsPage.finance.lateEnding.empty',
      },
      {
        titleKey: 'projectsPage.finance.completed.title',
        value: rows.filter((row) => row.status === 'completed').length,
        items: rows.filter((row) => row.status === 'completed').slice(0, 4),
        emptyMessageKey: 'projectsPage.finance.completed.empty',
      },
    ] satisfies FinanceCardVm[]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setSearchQuery(value: string) {
    this.searchQuery.set(value);
  }

  setStatusFilter(value: 'all' | ProjectStatus) {
    this.statusFilter.set(value);
  }

  setClientFilter(value: string) {
    this.clientFilter.set(value);
  }

  setSiteFilter(value: string) {
    this.siteFilter.set(value);
  }

  setOwnerFilter(value: string) {
    this.ownerFilter.set(value);
  }

  setActiveTab(tab: OverviewTab) {
    this.activeTab.set(tab);
  }

  onSectionTabChange(tab: string) {
    this.setActiveTab(tab as OverviewTab);
  }

  setCalendarView(value: 'day' | 'week') {
    this.calendarView.set(value);
  }

  onDateChange(date: Date) {
    if (date != null) {
      this.selectedDay.set(date);
    }
  }

  previousCalendarPeriod() {
    this.selectedDay.update((value) => this.calendarView() === 'day'
      ? addDays(value, -1)
      : addWeeks(value, -1));
  }

  nextCalendarPeriod() {
    this.selectedDay.update((value) => this.calendarView() === 'day'
      ? addDays(value, 1)
      : addWeeks(value, 1));
  }

  onProject(project: SiteProject) {
    this.navigationService.navigate(['/projects', project.guid]);
  }

  onInsightCard(item: InsightCardItem) {
    if (item.key === 'billable') {
      this.setActiveTab('finance');
      return;
    }

    if (item.key === 'active' || item.key === 'upcoming' || item.key === 'late' || item.key === 'completed') {
      this.setStatusFilter(item.key as ProjectStatus);
      this.setActiveTab('overview');
    }
  }

  private buildProjectRow(project: SiteProject, sites: Site[], clients: Client[]): Observable<ProjectRowVm> {
    const site = sites.find((item) => item.guid === project.site) ?? null;
    const client = clients.find((item) => item.guid === (project.client || site?.client)) ?? null;
    const responsible = project.contractorContacts?.[0]?.name
      ?? project.clientContacts?.[0]?.name
      ?? site?.sitelead?.[0]?.guid
      ?? '-';

    return combineLatest([
      this.siteProjectPeriodsService.getList({ project: project.guid }).pipe(startWith([] as SiteProjectPeriod[])),
      this.workerTicketsService.getProjectTickets(project).pipe(startWith([])),
    ]).pipe(
      map(([periods, tickets]) => {
        const phases = periods.map((period) => this.buildPhaseVm(period, tickets));
        const totalTasks = phases.reduce((sum, phase) => sum + phase.totalTasks, 0);
        const completedTasks = phases.reduce((sum, phase) => sum + phase.completedTasks, 0);
        const startDate = this.toDate(project.start) ?? this.getRangeBoundary(phases, 'start');
        const endDate = this.toDate(project.end) ?? this.getRangeBoundary(phases, 'end');
        const status = this.getProjectStatus(phases, startDate, endDate);
        return {
          project,
          site,
          client,
          clientGuid: client?.guid ?? null,
          siteGuid: site?.guid ?? null,
          clientName: client?.name ?? '-',
          siteName: site?.name ?? site?.project ?? '-',
          responsible,
          startDate,
          endDate,
          status,
          financialState: project.totalSum > 0 ? 'billable' : 'notBillable',
          progressPercent: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
          completedTasks,
          totalTasks,
          phases,
        } satisfies ProjectRowVm;
      }),
    );
  }

  private buildPhaseVm(period: SiteProjectPeriod, tickets: { projectPeriod?: string; status: TicketStatus }[]): ProjectPhaseVm {
    const phaseTickets = tickets.filter((ticket) => ticket.projectPeriod === period.guid);
    const totalTasks = Math.max(period.tickets?.length ?? 0, phaseTickets.length);
    const completedTasks = phaseTickets.filter((ticket) => [TicketStatus.done, TicketStatus.checked].includes(ticket.status)).length;
    const startDate = this.toDate(period.start);
    const endDate = this.toDate(period.end);
    return {
      guid: period.guid,
      name: period.name,
      startDate,
      endDate,
      status: this.getPhaseStatus(startDate, endDate, totalTasks, completedTasks),
      totalTasks,
      completedTasks,
    };
  }

  private getPhaseStatus(
    startDate: Date | null,
    endDate: Date | null,
    totalTasks: number,
    completedTasks: number,
  ): PhaseStatus {
    const now = new Date();
    if (totalTasks > 0 && completedTasks >= totalTasks) {
      return 'completed';
    }
    if (endDate && endDate < now) {
      return 'late';
    }
    if (startDate && startDate > now) {
      return 'upcoming';
    }
    return 'active';
  }

  private getProjectStatus(phases: ProjectPhaseVm[], startDate: Date | null, endDate: Date | null): ProjectStatus {
    if (phases.some((phase) => phase.status === 'late')) {
      return 'late';
    }
    if (phases.length > 0 && phases.every((phase) => phase.status === 'completed')) {
      return 'completed';
    }
    if (phases.some((phase) => phase.status === 'active')) {
      return 'active';
    }
    if (phases.length > 0) {
      return 'upcoming';
    }

    const now = new Date();
    if (endDate && endDate < now) {
      return 'completed';
    }
    if (startDate && startDate > now) {
      return 'upcoming';
    }
    return 'active';
  }

  private getRangeBoundary(phases: ProjectPhaseVm[], type: 'start' | 'end') {
    const dates = phases
      .map((phase) => type === 'start' ? phase.startDate : phase.endDate)
      .filter((date): date is Date => date instanceof Date);
    if (dates.length === 0) {
      return null;
    }
    return new Date(type === 'start'
      ? Math.min(...dates.map((date) => date.getTime()))
      : Math.max(...dates.map((date) => date.getTime())));
  }

  private toDate(value: unknown): Date | null {
    const date = convertTimestamp(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
}
